/**
 * Activity Entries API services
 *
 * Handles CRUD for timestamped activity entries (notes/comments)
 * on templates and task instances.
 */

import apiClient from './client';

// ============================================================================
// Types
// ============================================================================

export interface ActivityEntry {
  id: string;
  entityType: string;
  entityId: string;
  content: string;
  createdBy: string;
  creatorName: string | null;
  creatorAvatarUrl: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateActivityEntry {
  entity_type: string;
  entity_id: string;
  content: string;
}

export interface UpdateActivityEntry {
  content: string;
}

// ============================================================================
// Transform
// ============================================================================

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const transformEntry = (data: Record<string, any>): ActivityEntry => ({
  id: data.id,
  entityType: data.entityType || data.entity_type,
  entityId: data.entityId || data.entity_id,
  content: data.content,
  createdBy: data.createdBy || data.created_by,
  creatorName: data.creatorName || data.creator_name || null,
  creatorAvatarUrl: data.creatorAvatarUrl || data.creator_avatar_url || null,
  createdAt: data.createdAt || data.created_at,
  updatedAt: data.updatedAt || data.updated_at,
});

// ============================================================================
// API
// ============================================================================

export const activityEntriesApi = {
  /**
   * List activity entries for an entity
   */
  list: async (entityType: string, entityId: string): Promise<ActivityEntry[]> => {
    const response = await apiClient.get('/v1/activity-entries', {
      params: { entity_type: entityType, entity_id: entityId },
    });
    const items = response.data.items || [];
    return items.map(transformEntry);
  },

  /**
   * Create a new activity entry
   */
  create: async (data: CreateActivityEntry): Promise<ActivityEntry> => {
    const response = await apiClient.post('/v1/activity-entries', data);
    return transformEntry(response.data);
  },

  /**
   * Update an activity entry (creator only)
   */
  update: async (id: string, data: UpdateActivityEntry): Promise<ActivityEntry> => {
    const response = await apiClient.put(`/v1/activity-entries/${id}`, data);
    return transformEntry(response.data);
  },

  /**
   * Delete an activity entry (creator only)
   */
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/v1/activity-entries/${id}`);
  },
};
